import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Confirm, Report } from 'notiflix'
import api from '../../../helpers/apiRequester'
import { Banner, Button, Card, Checkbox, Field, FieldGrid, InfoTip, Input, NativeSelect, ProgressBar, StatusPill, Switch } from '../../../ui'
import styles from '../commsShared/admin.module.scss'
import { LINKING_EXPLAINER, LINK_STATE_MEANING, describeUnlinkedReason, linkStateLabel, unlinkedReasonLabel } from '../commsShared/linkStates'
import tl from './pipeline.module.scss'

type JobName = 'sweep' | 'preprocess' | 'identity' | 'extract' | 'summarise' | 'book' | 'reconcile'
type StageStatus = 'running' | 'done' | 'pending' | 'notReached'

interface Progress {
    batches: number
    processed: number
    ok: number
    failed: number
    skipped: number
    remaining: number | null
    costPence: number
    startedAt: string
    lastBatchAt: string | null
    heartbeatAt?: string | null
    note: string | null
    live?: {
        fetched: number
        ingested: number
        deduped: number
        errors: number
        pages: number
        folder: string | null
        activity?: {
            folder: string
            phase: 'page' | 'message' | 'throttled'
            messageId: string | null
            waitMs?: number | null
            step?: string | null
            forSeconds: number
            stepForSeconds?: number
        } | null
    } | null
    lastPass?: { at: string; fetched: number; ingested: number; deduped: number; linked: number; errors: number } | null
    report?: Record<string, any> | null
}

interface Job {
    name: JobName
    state: 'idle' | 'running' | 'stopping'
    leaseOwner: string | null
    params: Record<string, unknown> | null
    progress: Progress | null
    startedAt: string | null
    finishedAt: string | null
    finishedReason: 'complete' | 'stopped' | 'error' | 'budget' | null
    lastError: string | null
    startedBy: string | null
    runningHere: boolean
}

interface Run {
    id: number
    name: JobName
    owner: string | null
    startedBy: string | null
    startedAt: string
    finishedAt: string
    finishedReason: 'complete' | 'stopped' | 'error' | 'budget'
    params: Record<string, unknown> | null
    progress: Progress | null
    lastError: string | null
}

interface Queues {
    folders_to_sweep: number
    items_to_sweep: number
    sweep_fetched: number
    sweep_total: number
    preprocess_pending: number
    sends_without_case: number
    claims_without_identity: number
    extraction_pending: number
    extraction_failed: number
    extraction_done: number
    unlinked: number
    with_candidates: number
    summarise_pending?: number | null
}

interface MailboxRow {
    address: string
    display_name: string | null
    enabled: number
    provider: string
    /** Mail arrives by webhook (SES); nothing to sweep. */
    live: boolean
    folder_count: number
    included_folders: number
    /** Unique emails: what the mailbox holds once swept (stored_count + missing_count). Same figure as the Mailboxes page. */
    estimated_total: number
    fetched_count: number
    /** Unique emails downloaded for this mailbox; an email filed in three folders is 1. */
    stored_count: number
    /** Folder items Graph reports that we have not seen yet. */
    missing_count: number
    /** Raw Graph folder-item sum (copies counted). Never a headline figure. */
    folder_items?: number
    ingested_count: number
    deduped_count: number
    noise_count: number
    linked_count: number
    error_count: number
    backfilling_folders: number
    delta_folders: number
    failed_folders: number
    paused_folders: number
    pending_folders: number
    last_synced_at: string | null
    last_error: string | null
}

interface Detail {
    mailboxes: MailboxRow[]
    preprocess: {
        byState: { state: string; total: number }[]
        byMailbox: { mailbox: string; total: number; ready: number; outstanding: number }[]
        recentErrors: { id: string; subject: string | null; preprocess_error: string | null }[]
    }
    identity: {
        coverage: { claims_with_sends: number; claims_with_identity: number; identity_errors: number }
        cases: { total: number; backfilled: number }
        casesByStatus: { status: string; total: number }[]
        sends: { total: number; without_case: number }
    }
    extraction: {
        byState: { state: string; total: number }[]
        byLink: { state: string; reason: string; total: number }[]
        byCategory: { category: string; total: number }[]
        disputed: { total: number }
        cost: {
            day_pence: number
            week_pence: number
            month_pence: number
            all_pence: number
            calls: number
            errors: number
            cached: number
        }
    }
    reconcile: {
        unlinked: { reason: string; total: number }[]
        pendingConversations: { total: number; expired: number | null } | null
    }
    summarise?: {
        coverage: {
            conversations?: number
            active?: number
            summarised?: number
            due?: number
            stale?: number
            single_message?: number
            last_summary_at?: string | null
        }
        recent: {
            id: string
            mailbox: string | null
            subject: string | null
            counterparty_address: string | null
            claim_ref: string | null
            summary_model: string | null
            summary_at: string
            message_count: number
            suggested_next_action: string | null
        }[]
        error?: string
    }
    ai?: AiActivity | { error: string }
    book?: {
        sources: {
            source_key: string
            schema_family: string
            watermark_at: string | null
            last_run_at: string | null
            last_run_ms: number | null
            last_rows: number | null
            last_claims: number | null
            last_error: string | null
            runs: number
        }[]
        identity: { rows_total: number; from_book: number; last_snapshot_at: string | null } | null
        error?: string
    }
}

interface AiModelRow {
    task: string
    stage: JobName | null
    model_key: string
    provider: string
    model_id: string
    role: string
    calls: number
    ok: number
    cached: number
    failed: number
    input_tokens: number
    output_tokens: number
    cost_pence: number
    cost_pence_24h: number
    avg_latency_ms: number | null
    calls_24h: number
    last_call_at: string | null
}
interface AiTaskRow {
    task: string
    stage: JobName | null
    calls: number
    subjects: number
    input_tokens: number
    output_tokens: number
    cost_pence: number
    calls_24h: number
    cost_pence_24h: number
    cost_pence_7d: number
    cost_pence_30d: number
    last_call_at: string | null
}
interface AiRoute {
    task: string
    primary_model: string | null
    pair_model: string | null
    fallback_model: string | null
    tiebreak_model: string | null
    escalation_model: string | null
    voting: string
    prompt_version: string
    schema_version: string
    max_output_tokens: number
    cache_identical_inputs: number
    active: number
    notes: string | null
}
interface AiModel {
    key: string
    provider: string
    model_id: string
    display_name: string
    tier: string
    input_cost_per_mtok_usd: number
    output_cost_per_mtok_usd: number
    active: number
}
interface AiActivity {
    byModel: AiModelRow[]
    byTask: AiTaskRow[]
    routes: AiRoute[]
    models: AiModel[]
    textract: {
        attachments: number
        pages: number
        attachments_24h: number
        pages_24h: number
        est_pence: number
        est_pence_24h: number
        pence_per_page: number
    } | null
    usdToGbp: number
}

interface Schedule {
    key: 'sweep' | 'reconcile'
    rule: string
    route: string
    drives: JobName[]
    exists: boolean
    enabled: boolean | null
    schedule: string | null
    description: string | null
    error: string | null
}

interface Status {
    owner: string
    mailbox: string | null
    jobs: Job[]
    queues: Queues
    detail: Detail
    runs: Run[]
    automation: {
        schedules: Schedule[]
        error: string | null
        lastCronRuns: { name: JobName; finished_at: string; finished_reason: string }[]
    }
    autoExtract: { enabled: boolean; source: string; secretName: string }
    threadSummary?: { enabled: boolean; source: string; secretName: string }
    runner?: { value: 'api' | 'worker'; source: string; secretName: string }
    /** The production kill switch as this API sees it. */
    pause?: { paused: boolean; by: string | null; at: string | null; source: string; secretName: string }
    worker?: {
        runner: 'api' | 'worker'
        runnerSource: string
        heartbeats: WorkerHeartbeat[]
        error: string | null
        live: number
    }
}

/** One Secrets Manager-backed settings group as described by comms/pipeline/settings. */
interface SettingsGroup {
    key: 'runtime' | 'worker'
    title: string
    secretName: string
    values: Record<string, string | number | boolean>
    /** field -> env var name when this API process overrides the stored value. */
    envOverrides: Record<string, string>
    /** What this API is actually using: the env value where overridden, otherwise the stored one. */
    effective?: Record<string, unknown>
    /** keys kept in the secret but never shown (apiKey). */
    hidden: Record<string, { set: boolean }>
    error: string | null
}

interface SettingsResponse {
    groups: { runtime: SettingsGroup; worker: SettingsGroup }
    workerLoops: string[]
}

interface WorkerDraft {
    enabled: boolean
    loops: string[] | 'all' | 'none'
    pollMs: string
    heartbeatMs: string
    baseUrl: string
}

/** The worker URL in force on this API (env override first, then the secret). Shown read-only. */
const workerBaseUrl = (settings: SettingsResponse): string => String(settings.groups.worker.effective?.baseUrl ?? settings.groups.worker.values.baseUrl ?? '')

/** The pipeline runner in force on this API (env override first, then the secret). */
const effectiveRunner = (settings: SettingsResponse): string => String(settings.groups.runtime.effective?.pipelineRunner ?? settings.groups.runtime.values.pipelineRunner ?? 'api')

const draftFromGroup = (g: SettingsGroup): WorkerDraft => {
    const loops = String(g.values.loops ?? 'all')
    return {
        enabled: Boolean(g.values.enabled),
        loops: loops === 'all' || loops === 'none' ? loops : loops.split(',').filter(Boolean),
        pollMs: String(g.values.pollMs ?? 30000),
        heartbeatMs: String(g.values.heartbeatMs ?? 60000),
        baseUrl: String(g.values.baseUrl ?? ''),
    }
}

interface WorkerLoopStatus {
    name: string
    job: string
    state: string
    enabled: boolean
    runs: number
    lastStartedAt: string | null
    lastFinishedAt: string | null
    lastDurationMs: number | null
    lastResult: { finishedReason?: string | null; processed?: number | null; failed?: number | null; remaining?: number | null; skipped?: string } | null
    lastError: string | null
    nextAt: string | null
    note: string | null
}

interface WorkerHeartbeat {
    workerId: string
    host: string | null
    version: string | null
    coreVersion: string | null
    loops: Record<string, WorkerLoopStatus> | null
    queueDepths: Record<string, number> | null
    lastError: string | null
    startedAt: string | null
    beatAt: string
    ageSeconds: number
    stale: boolean
}

interface ExtractParams {
    batchSize: number
    concurrency: number
    maxItems: string
    maxCostPounds: string
    states: string[]
    voting: '' | 'single' | 'pair' | 'pair_plus_tiebreak'
}

interface SummariseParams {
    maxItems: string
    maxCostPounds: string
}
const EST_PENCE_PER_THREAD = 0.5 // small model, one call per thread; a guide only

const fmt = (n: number | null | undefined) => (n == null ? '—' : Number(n).toLocaleString('en-GB'))
const pounds = (pence: number | null | undefined) => `£${(Number(pence || 0) / 100).toFixed(2)}`
const pct = (part: number, whole: number) => (whole > 0 ? Math.min(100, Math.round((part / whole) * 100)) : 0)
const fmtDate = (raw: string | null | undefined) =>
    raw
        ? new Date(raw).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
        : '—'
const ago = (raw: string | null | undefined, now: number) => {
    if (!raw) return '—'
    const s = Math.max(0, Math.round((now - new Date(raw).getTime()) / 1000))
    if (s < 60) return `${s}s ago`
    if (s < 3600) return `${Math.round(s / 60)}m ago`
    if (s < 86400) return `${Math.round(s / 3600)}h ago`
    return `${Math.round(s / 86400)}d ago`
}
const durationOf = (a: string, b: string) => {
    const s = Math.max(0, Math.round((new Date(b).getTime() - new Date(a).getTime()) / 1000))
    return s < 60
        ? `${s}s`
        : s < 3600
          ? `${Math.floor(s / 60)}m ${s % 60}s`
          : `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`
}
const EST_PENCE_PER_EMAIL = 0.6 // observed pair-voting average; a guide only
const RECONCILE_INTERVAL_MIN = 5

const STAGES: { name: JobName; step: number; title: string; what: string; detail?: ReactNode; cloud: string; ai?: 'llm' | 'ocr' }[] = [
    {
        name: 'sweep',
        step: 1,
        title: 'Sweep mailboxes',
        what: 'Pulls every email from the included folders of each shared mailbox via Microsoft Graph, de-duplicates on Graph id, and stores the raw message and attachments. Backfills folder by folder, then keeps them current with delta polling.',
        cloud: 'comms-ingest-mailboxes rule → one 4-minute pass every 5 minutes',
    },
    {
        name: 'preprocess',
        step: 2,
        title: 'Preprocess',
        what: 'Strips quoted history, signatures and HTML, extracts attachment text (pdf.js first, AWS Textract OCR for scans and images), and runs the cheap deterministic matcher (claim ref, message-id, lender domain). No LLM calls; Textract is billed per page by AWS.',
        cloud: 'comms-reconcile rule → stragglers each pass',
        ai: 'ocr',
    },
    {
        name: 'identity',
        step: 3,
        title: 'Case identity',
        what: 'Builds one comms_case per (claim, lender) from the LOC send history, then snapshots client identity (name, vehicle, agreement) from the main database so replies without a reference can still be matched.',
        cloud: 'comms-reconcile rule → top-up for new sends',
    },
    {
        name: 'extract',
        step: 4,
        title: 'Extract & categorise',
        what: 'Structured LLM extraction with pair voting (each model’s answer recorded, disagreements flagged), category assignment, rule evaluation and final linking. This is the paid step.',
        cloud: 'comms-reconcile rule → only while Automatic extraction is ON',
        ai: 'llm',
    },
    {
        name: 'summarise',
        step: 5,
        title: 'Summarise threads',
        what: 'One short paragraph per conversation (our letter, every reply, our follow-ups), refreshed whenever a new message lands. Written for a handler opening the thread cold; it never changes a thread’s state or links. A small-model call per thread, so this is a paid step.',
        cloud: 'comms worker summarise loop → every 2 minutes while Thread summaries are ON',
        ai: 'llm',
    },
    {
        name: 'book',
        step: 6,
        title: 'Client book index',
        what: 'Copies every claim in the client book (introducer databases, ZappedCases, oaGenieSync) into the comms identity index so a reply can be linked to any client we hold, not only ones we have written to. Each pass copies rows changed since the previous pass; the source databases are only ever read.',
        cloud: 'comms worker book loop → every 2 minutes',
    },
    {
        name: 'reconcile',
        step: 7,
        title: 'Reconcile',
        what: 'Housekeeping, not a stage of its own. Every pass does three things:',
        detail: (
            <ul className={tl.stageList}>
                <li>
                    <strong>Retries the linking</strong> for emails still without a client or claim. New clients and claims join the index all the time (new LOC sends, the client book copy), so an email that would not match last week may match today.
                </li>
                <li>
                    <strong>Closes stale threads</strong> that have waited too long for a reply, so they stop counting as pending.
                </li>
                <li>
                    <strong>Catches stragglers</strong>: anything the earlier stages skipped or failed on goes through them again.
                </li>
            </ul>
        ),
        cloud: 'comms-reconcile rule → every 5 minutes',
    },
]

const LINK_STATE_ORDER = ['linked', 'candidates', 'unlinked', 'not_applicable']

/** Fold the (state, reason, total) rows into one group per link state with reason sub-rows. */
const linkStateGroups = (rows: { state: string; reason: string; total: number }[]) => {
    const groups = new Map<string, { state: string; total: number; reasons: { reason: string; total: number }[] }>()
    for (const r of rows) {
        if (!groups.has(r.state)) groups.set(r.state, { state: r.state, total: 0, reasons: [] })
        const g = groups.get(r.state)!
        g.total += Number(r.total)
        if (r.state !== 'linked') g.reasons.push({ reason: r.reason, total: Number(r.total) })
    }
    const list = Array.from(groups.values())
    list.forEach((g) => g.reasons.sort((a, b) => b.total - a.total))
    return list.sort((a, b) => (LINK_STATE_ORDER.indexOf(a.state) + 1 || 99) - (LINK_STATE_ORDER.indexOf(b.state) + 1 || 99))
}

const AI_STAGE: Record<string, JobName> = {
    'comms.classify_noise': 'extract',
    'comms.extract': 'extract',
    'comms.extract_long': 'extract',
    'comms.summarise_thread': 'extract',
}

const STATUS_LABEL: Record<StageStatus, string> = {
    running: 'in progress',
    done: 'done',
    pending: 'pending',
    notReached: 'not reached',
}
const STATUS_PILL: Record<StageStatus, 'good' | 'info' | 'bad' | 'neutral'> = {
    running: 'good',
    done: 'info',
    pending: 'bad',
    notReached: 'neutral',
}
const reasonTone = (r: Run['finishedReason'] | null) =>
    r === 'complete' ? 'good' : r === 'error' ? 'bad' : r === 'budget' ? 'warn' : 'neutral'

export const CommsPipelineView = () => {
    const [status, setStatus] = useState<Status | null>(null)
    const [busy, setBusy] = useState<string | null>(null)
    const [selected, setSelected] = useState<JobName | null>(null)
    const [aiOpen, setAiOpen] = useState<boolean>(() => localStorage.getItem('comms.pipeline.aiOpen') === '1')
    const toggleAi = () => {
        const next = !aiOpen
        setAiOpen(next)
        localStorage.setItem('comms.pipeline.aiOpen', next ? '1' : '0')
    }
    const [extractParams, setExtractParams] = useState<ExtractParams>({
        batchSize: 25,
        concurrency: 4,
        maxItems: '50',
        maxCostPounds: '',
        states: ['pending', 'failed'],
        voting: '',
    })
    const [summariseParams, setSummariseParams] = useState<SummariseParams>({ maxItems: '50', maxCostPounds: '' })
    // Scope: '' = every mailbox, otherwise one address. Queues, metrics and the
    // Run buttons for sweep / preprocess / extract all follow it, so a small
    // mailbox can be taken end-to-end before the big ones.
    const [scope, setScope] = useState<string>(() => window.localStorage.getItem('comms.pipeline.scope') || '')
    const [now, setNow] = useState(() => Date.now())
    // Stale heartbeats (old local boots, redeployed instances) are hidden by default;
    // the count is shown and they can be expanded on demand.
    const [showStaleWorkers, setShowStaleWorkers] = useState(false)
    // Comms settings (Secrets Manager) shown at the bottom of the page.
    const [settings, setSettings] = useState<SettingsResponse | null>(null)
    const [settingsError, setSettingsError] = useState<string | null>(null)
    // null = not yet known; false = the API said 403 (not a Sentinel admin) and the section is not rendered.
    const [settingsAllowed, setSettingsAllowed] = useState<boolean | null>(null)
    const [settingsOpen, setSettingsOpen] = useState(false)
    const [workerDraft, setWorkerDraft] = useState<WorkerDraft | null>(null)
    const [workerDirty, setWorkerDirty] = useState(false)
    const liveWorkers = useMemo(() => (status?.worker?.heartbeats || []).filter((h) => !h.stale), [status])
    const staleWorkers = useMemo(() => (status?.worker?.heartbeats || []).filter((h) => h.stale), [status])
    // The SES mailbox is a live webhook stream: it can be scoped for preprocess /
    // extract / reconcile like any other, but there is nothing to sweep.
    const scopeIsLive = Boolean(scope && status?.detail.mailboxes.find((m) => m.address === scope)?.live)

    // One status request at a time. The endpoint is an expensive fan-out; if a
    // poll is still in flight when the timer fires again, skip this tick rather
    // than stack another copy behind it (that stacking is what took the page
    // down in Sept 2026).
    const loadInFlight = useRef(false)
    const load = useCallback(async () => {
        if (loadInFlight.current) return
        loadInFlight.current = true
        try {
            const resp = await api({ url: 'comms/pipeline/status', data: { mailbox: scope || null } })
            if (resp.data?.success) setStatus(resp.data)
            setNow(Date.now())
        } catch (err: any) {
            Report.failure('Load failed', err?.response?.data?.message || 'Could not load the pipeline.', 'Okay')
        } finally {
            loadInFlight.current = false
        }
    }, [scope])

    useEffect(() => {
        window.localStorage.setItem('comms.pipeline.scope', scope)
    }, [scope])

    const loadSettings = useCallback(async () => {
        try {
            const resp = await api({ url: 'comms/pipeline/settings', data: {} })
            if (resp.data?.success) {
                setSettings(resp.data)
                setSettingsAllowed(true)
                setSettingsError(null)
                setWorkerDraft((prev) => (prev && workerDirty ? prev : draftFromGroup(resp.data.groups.worker)))
            } else setSettingsError(resp.data?.message || 'Could not load the settings.')
        } catch (err: any) {
            if (err?.response?.status === 403) {
                setSettingsAllowed(false)
                return
            }
            setSettingsAllowed(true)
            setSettingsError(err?.response?.data?.message || 'Could not load the settings.')
        }
    }, [workerDirty])

    useEffect(() => {
        loadSettings()
    }, [loadSettings])

    const saveSettings = async (group: 'runtime' | 'worker', patch: Record<string, unknown>, label: string) => {
        setBusy(label)
        try {
            const resp = await api({ url: 'comms/pipeline/settings-update', data: { group, patch } })
            if (resp.data?.success === false) Report.warning(label, resp.data.message || 'Did not save.', 'Okay')
            else if (group === 'worker') setWorkerDirty(false)
            await loadSettings()
            await load()
            return resp.data
        } catch (err: any) {
            Report.failure(`${label} failed`, err?.response?.data?.message || 'Please try again.', 'Okay')
        } finally {
            setBusy(null)
        }
    }

    const editDraft = (patch: Partial<WorkerDraft>) => {
        setWorkerDraft((d) => (d ? { ...d, ...patch } : d))
        setWorkerDirty(true)
    }
    const toggleLoop = (name: string, on: boolean) => {
        if (!workerDraft || !settings) return
        const all = settings.workerLoops
        const current = workerDraft.loops === 'all' ? all : workerDraft.loops === 'none' ? [] : workerDraft.loops
        const next = on ? (current.includes(name) ? current : [...current, name]) : current.filter((l) => l !== name)
        editDraft({ loops: next.length === all.length ? 'all' : next.length === 0 ? 'none' : all.filter((l) => next.includes(l)) })
    }

    useEffect(() => {
        load()
    }, [load])

    const anyRunning = useMemo(() => Boolean(status?.jobs.some((j) => j.state !== 'idle' && j.leaseOwner)), [status])

    // Poll faster while a job runs, never while the tab is hidden (a backgrounded
    // tab kept hammering the endpoint all morning), and catch up on return.
    useEffect(() => {
        const tick = () => {
            if (document.visibilityState === 'hidden') return
            load()
        }
        const t = setInterval(tick, anyRunning ? 10000 : 30000)
        document.addEventListener('visibilitychange', tick)
        return () => {
            clearInterval(t)
            document.removeEventListener('visibilitychange', tick)
        }
    }, [anyRunning, load])

    const post = async (label: string, url: string, data: Record<string, unknown>) => {
        setBusy(label)
        try {
            const resp = await api({ url, data })
            if (resp.data?.success === false) Report.warning(label, resp.data.message || 'Did not complete.', 'Okay')
            await load()
            return resp.data
        } catch (err: any) {
            Report.failure(`${label} failed`, err?.response?.data?.message || 'Please try again.', 'Okay')
        } finally {
            setBusy(null)
        }
    }

    const resync = (m: MailboxRow) =>
        Confirm.show(
            'Re-sync mailbox',
            `Walk every included folder of ${m.display_name || m.address} again from the start. Mail already downloaded is recognised by its Graph id and skipped; the ${fmt(m.missing_count)} email(s) still to download are picked up. Then run Sweep${scope === m.address ? '' : ` with scope set to ${m.address}`}.`,
            'Re-sync',
            'Cancel',
            () => post(`Re-sync ${m.address}`, 'comms/mailboxes/resync', { mailbox: m.address }),
        )

    const scoped = (job: JobName) => ['sweep', 'preprocess', 'extract', 'summarise'].includes(job)
    const start = (job: JobName, params: Record<string, unknown> = {}) =>
        post(`Run ${job}`, 'comms/pipeline/start', {
            job,
            params: { ...(scoped(job) && scope ? { mailbox: scope } : {}), ...params },
        })
    const stop = (job: JobName) => post(`Stop ${job}`, 'comms/pipeline/stop', { job })

    const q = status?.queues
    const d = status?.detail
    const jobFor = (name: JobName) => status?.jobs.find((j) => j.name === name)
    const running = (j?: Job) => Boolean(j && j.state !== 'idle' && j.leaseOwner)
    const interrupted = (j?: Job) => Boolean(j && j.state !== 'idle' && !j.leaseOwner)

    /**
     * The kill switch. Stopping asks every running job to finish its batch and
     * holds everything after that: the worker's loops, the cloud ticks, the Run
     * buttons and reading new mail as it arrives. Mail is still received and
     * stored. Starting again lets the next tick or loop pass carry on.
     */
    const paused = Boolean(status?.pause?.paused)
    const pauseFromEnv = status?.pause?.source === 'env'
    const togglePause = () => {
        const runningNow = (status?.jobs || []).filter((j) => running(j)).map((j) => j.name)
        if (paused) {
            Confirm.show(
                'Start the production pipeline',
                'The worker loops, the cloud schedules, the Run buttons and reading new mail as it arrives all resume within a minute. Any laptop still running the pipeline with COMMS_PIPELINE_PAUSED=0 will then be competing for the same jobs.',
                'Start pipeline',
                'Cancel',
                () => post('Start production pipeline', 'comms/pipeline/pause', { paused: false }),
            )
            return
        }
        Confirm.show(
            'Stop the production pipeline',
            `Nothing in production starts a pipeline job until it is started again: the worker loops idle, the cloud schedules and Run buttons are refused, and new mail is stored but not read.${
                runningNow.length ? ` ${runningNow.join(', ')} ${runningNow.length === 1 ? 'is' : 'are'} running now and will be asked to stop after the current batch.` : ''
            } Mail keeps arriving and is kept. Use this to run the pipeline from your own machine.`,
            'Stop pipeline',
            'Cancel',
            () => post('Stop production pipeline', 'comms/pipeline/pause', { paused: true }),
        )
    }

    /** Outstanding work per stage — what the timeline box counts. */
    const queueFor = (name: JobName): { value: number; label: string } => {
        if (!q) return { value: 0, label: '' }
        switch (name) {
            case 'sweep':
                return { value: Number(q.items_to_sweep), label: 'items not yet held' }
            case 'preprocess':
                return { value: Number(q.preprocess_pending), label: 'emails to preprocess' }
            case 'identity':
                return { value: Number(q.claims_without_identity), label: 'claims without identity' }
            case 'extract':
                return { value: Number(q.extraction_pending), label: 'emails to extract' }
            case 'reconcile':
                return { value: Number(q.unlinked), label: 'unlinked emails' }
            case 'summarise':
                return { value: Number(q.summarise_pending ?? 0), label: 'threads awaiting a summary' }
            case 'book':
                return { value: Number(d?.book?.identity?.rows_total ?? 0), label: 'claims in the index' }
        }
    }

    const lastReconcileAge = (() => {
        const j = jobFor('reconcile')
        return j?.finishedAt ? (now - new Date(j.finishedAt).getTime()) / 60000 : null
    })()

    const stageStatus = (name: JobName): StageStatus => {
        const j = jobFor(name)
        if (running(j)) return 'running'
        if (name === 'reconcile') {
            // Reconcile is a heartbeat rather than a queue: it's "done" if it ran recently.
            if (lastReconcileAge != null && lastReconcileAge <= RECONCILE_INTERVAL_MIN * 3) return 'done'
            return j?.finishedAt || Number(q?.unlinked) > 0 ? 'pending' : 'notReached'
        }
        const outstanding = queueFor(name).value
        if (outstanding === 0) return 'done'
        const idx = STAGES.findIndex((s) => s.name === name)
        const previousDone = idx === 0 || stageStatus(STAGES[idx - 1].name) === 'done'
        if (!previousDone && !j?.finishedAt) return 'notReached'
        return 'pending'
    }

    const startExtract = () => {
        const maxItems = Number(extractParams.maxItems) || null
        const candidates =
            (q?.extraction_pending || 0) + (extractParams.states.includes('failed') ? q?.extraction_failed || 0 : 0)
        const willDo = maxItems ? Math.min(maxItems, candidates) : candidates
        const capLine = extractParams.maxCostPounds
            ? ` Spending stops at £${Number(extractParams.maxCostPounds).toFixed(2)}.`
            : ''
        Confirm.show(
            'Run extraction',
            `Extract and categorise up to ${fmt(willDo)} email(s)${scope ? ` from ${scope}` : ' across every mailbox'} with ${extractParams.voting || 'the routed'} voting. Estimated cost about ${pounds(willDo * EST_PENCE_PER_EMAIL)} (guide only).${capLine} You can stop at any time; finished rows are kept.`,
            'Run',
            'Cancel',
            () =>
                start('extract', {
                    batchSize: extractParams.batchSize,
                    concurrency: extractParams.concurrency,
                    maxItems,
                    maxCostPounds: Number(extractParams.maxCostPounds) || null,
                    states: extractParams.states,
                    voting: extractParams.voting || null,
                }),
        )
    }

    const startSummarise = () => {
        const maxItems = Number(summariseParams.maxItems) || null
        const due = Number(q?.summarise_pending ?? 0)
        const willDo = maxItems ? Math.min(maxItems, due) : due
        const capLine = summariseParams.maxCostPounds
            ? ` Spending stops at £${Number(summariseParams.maxCostPounds).toFixed(2)}.`
            : ''
        const gate = status?.threadSummary?.enabled
            ? ''
            : ` Thread summaries are currently OFF (${status?.threadSummary?.secretName ?? 'comms-settings'}), so this run will finish immediately without writing anything; set threadSummaryEnabled to true first.`
        Confirm.show(
            'Summarise threads',
            `Write or refresh summaries for up to ${fmt(willDo)} thread(s)${scope ? ` in ${scope}` : ' across every mailbox'}, newest activity first. Estimated cost about ${pounds(willDo * EST_PENCE_PER_THREAD)} (guide only).${capLine}${gate} You can stop at any time; finished threads are kept.`,
            'Run',
            'Cancel',
            () =>
                start('summarise', {
                    maxItems,
                    maxCostPounds: Number(summariseParams.maxCostPounds) || null,
                }),
        )
    }

    const toggleSchedule = (s: Schedule, enabled: boolean) => {
        const verb = enabled ? 'Switch on' : 'Switch off'
        Confirm.show(
            `${verb} ${s.rule}`,
            enabled
                ? `EventBridge will POST ${s.route} on ${s.schedule}. Each tick takes the job lease, so it steps aside (409) while you run the same stage from here.`
                : `EventBridge will stop calling ${s.route}. Nothing already running is interrupted; the stages it drives (${s.drives.join(', ')}) will only run when you start them here.`,
            verb,
            'Cancel',
            () => post(`${verb} schedule`, 'comms/pipeline/automation', { schedule: s.key, enabled }),
        )
    }

    const selectedStage =
        selected ??
        STAGES.find((s) => stageStatus(s.name) === 'running')?.name ??
        STAGES.find((s) => stageStatus(s.name) === 'pending')?.name ??
        'sweep'
    const stage = STAGES.find((s) => s.name === selectedStage)!
    const job = jobFor(stage.name)
    const isRunning = running(job)
    const runsFor = (name: JobName) => (status?.runs || []).filter((r) => r.name === name).slice(0, 8)
    const lastCron = (name: JobName) => status?.automation.lastCronRuns.find((r) => r.name === name)

    const runLine = (j?: Job) => {
        if (!j) return null
        const p = j.progress
        if (running(j)) {
            const where = j.runningHere ? 'this server' : j.leaseOwner?.split(':')[0]
            const live = p?.live
            const act = live?.activity
            const doing = act
                ? act.phase === 'throttled'
                    ? ` · Graph throttled us (429) in ${act.folder} — all workers paused ${Math.max(0, Math.round((act.waitMs || 0) / 1000) - act.forSeconds)}s more`
                    : act.phase === 'page'
                      ? ` · now listing ${act.folder}${act.forSeconds > 20 ? ` — waiting on Graph for ${act.forSeconds}s (times out at 30s)` : ''}`
                      : ` · now on a message in ${act.folder}: ${act.step || 'starting'}${(act.stepForSeconds ?? act.forSeconds) > 30 ? ` for ${act.stepForSeconds ?? act.forSeconds}s (a message is abandoned after 5 min and counted as an error)` : ''}`
                : ''
            const inFlight = live
                ? ` · this pass so far: ${fmt(live.fetched)} fetched, ${fmt(live.ingested)} new, ${fmt(live.deduped)} already held${live.errors ? `, ${fmt(live.errors)} errors` : ''}${doing}`
                : ''
            const beatAge = p?.heartbeatAt ? Math.round((now - new Date(p.heartbeatAt).getTime()) / 1000) : null
            const beat =
                beatAge == null
                    ? ''
                    : beatAge > 90
                      ? ` · no heartbeat for ${beatAge}s — the process may have died; the lease frees itself after 2 minutes`
                      : ` · heartbeat ${beatAge}s ago`
            return `${j.state === 'stopping' ? 'Stopping — finishing the message in hand, then saving this page' : 'Running'} on ${where}${j.startedBy ? ` by ${j.startedBy}` : ''} since ${fmtDate(j.startedAt)} · batch ${(p?.batches ?? 0) + 1} · ${fmt(p?.processed)} processed in finished batches${p?.failed ? `, ${fmt(p.failed)} failed` : ''}${p?.costPence ? ` · ${pounds(p.costPence)}` : ''}${inFlight}${p?.note ? ` · ${p.note}` : ''}${beat}`
        }
        if (interrupted(j))
            return `Interrupted — the process running it stopped without finishing (last heartbeat ${fmtDate(j.progress?.heartbeatAt)}). Nothing is lost; Run resumes where it left off.`
        if (!j.finishedAt) return 'Never run.'
        return `Last run ${fmtDate(j.finishedAt)} (${j.startedBy || 'unknown'}) · ${j.finishedReason} · ${fmt(p?.processed)} processed, ${fmt(p?.ok)} ok, ${fmt(p?.failed)} failed${p?.costPence ? ` · ${pounds(p.costPence)}` : ''}${j.finishedReason === 'error' ? ` · ${j.lastError}` : p?.note ? ` · ${p.note}` : ''}`
    }

    const modelLabel = (key: string | null, models: AiModel[]) => {
        if (!key) return '—'
        const m = models.find((x) => x.key === key)
        return m ? `${m.display_name} (${m.provider})` : key
    }
    const routeLine = (r: AiRoute, models: AiModel[]) => {
        const parts = [`${r.voting}: ${modelLabel(r.primary_model, models)}`]
        if (r.pair_model && r.voting !== 'single') parts.push(`+ ${modelLabel(r.pair_model, models)}`)
        if (r.tiebreak_model && r.voting === 'pair_plus_tiebreak')
            parts.push(`tiebreak ${modelLabel(r.tiebreak_model, models)}`)
        if (r.fallback_model) parts.push(`fallback ${modelLabel(r.fallback_model, models)}`)
        return parts.join(' · ')
    }
    const fmtTok = (n: number) =>
        Number(n) >= 1_000_000
            ? `${(Number(n) / 1_000_000).toFixed(2)}M`
            : Number(n) >= 1000
              ? `${(Number(n) / 1000).toFixed(1)}k`
              : fmt(n)
    const penceStr = (p: number) => (Number(p) >= 100 ? pounds(p) : `${Number(p || 0).toFixed(2)}p`)
    const stageTitle = (name: JobName | null) =>
        name ? (STAGES.find((x) => x.name === name)?.title ?? name) : 'outside the pipeline'

    const aiCard = () => {
        const ai = status?.detail.ai
        if (!ai) return null
        if ('error' in ai)
            return (
                <Banner tone="warn" title="AI usage unavailable">
                    {ai.error}
                </Banner>
            )
        const pipelineTasks = ai.byTask.filter((t) => t.stage)
        const otherTasks = ai.byTask.filter((t) => !t.stage)
        const sum = (rows: AiTaskRow[], k: keyof AiTaskRow) => rows.reduce((a, r) => a + Number(r[k] || 0), 0)
        const pipelineRoutes = ai.routes.filter((r) =>
            ['comms.classify_noise', 'comms.extract', 'comms.extract_long', 'comms.summarise_thread'].includes(r.task),
        )
        const summary = `${fmt(sum(pipelineTasks, 'calls'))} LLM calls · ${penceStr(sum(pipelineTasks, 'cost_pence'))} spent${ai.textract ? ` · ${fmt(ai.textract.pages)} OCR pages ≈ ${penceStr(ai.textract.est_pence)}` : ''}`
        return (
            <Card
                title="AI & inference"
                subtitle={
                    aiOpen
                        ? "Every model call is written to the ledger (ai_inference_run) with the provider's own token usage and the price list at call time. Only Step 4 calls an LLM; Step 2 uses AWS Textract OCR, which is billed per page and estimated here."
                        : summary
                }
                actions={
                    <Button size="sm" variant="ghost" onClick={toggleAi} aria-expanded={aiOpen}>
                        {aiOpen ? 'Hide details' : 'Show details'}
                    </Button>
                }
                padding={aiOpen ? 'md' : 'sm'}
            >
                {aiOpen && (
                    <>
                        <KV
                            items={[
                                {
                                    label: 'LLM calls (pipeline)',
                                    value: `${fmt(sum(pipelineTasks, 'calls'))} · ${fmt(sum(pipelineTasks, 'calls_24h'))} in 24h`,
                                },
                                { label: 'emails extracted', value: fmt(sum(pipelineTasks, 'subjects')) },
                                {
                                    label: 'tokens in / out',
                                    value: `${fmtTok(sum(pipelineTasks, 'input_tokens'))} / ${fmtTok(sum(pipelineTasks, 'output_tokens'))}`,
                                },
                                {
                                    label: 'LLM spend 24h / 7d / 30d / all',
                                    value: `${penceStr(sum(pipelineTasks, 'cost_pence_24h'))} / ${penceStr(sum(pipelineTasks, 'cost_pence_7d'))} / ${penceStr(sum(pipelineTasks, 'cost_pence_30d'))} / ${penceStr(sum(pipelineTasks, 'cost_pence'))}`,
                                },
                                {
                                    label: 'Textract pages (est. cost)',
                                    value: ai.textract
                                        ? `${fmt(ai.textract.pages)} (${penceStr(ai.textract.est_pence)}) · ${fmt(ai.textract.pages_24h)} in 24h`
                                        : '—',
                                },
                                {
                                    label: 'automatic extraction',
                                    value: status?.autoExtract ? (status.autoExtract.enabled ? 'ON' : 'OFF') : '—',
                                },
                                { label: 'USD → GBP used', value: String(ai.usdToGbp) },
                            ]}
                        />
                        <div className={styles.tableWrap}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>Task</th>
                                        <th>Stage</th>
                                        <th>Configured route</th>
                                        <th className={tl.right}>Calls</th>
                                        <th className={tl.right}>Emails</th>
                                        <th className={tl.right}>Tokens in / out</th>
                                        <th className={tl.right}>Spend 24h</th>
                                        <th className={tl.right}>Spend all</th>
                                        <th>Last call</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pipelineRoutes.map((r) => {
                                        const t = ai.byTask.find((x) => x.task === r.task)
                                        return (
                                            <tr key={r.task}>
                                                <td>
                                                    <span className={tl.mono}>{r.task}</span>
                                                    {r.notes && <div className={styles.sub}>{r.notes}</div>}
                                                </td>
                                                <td>{stageTitle(AI_STAGE[r.task] ?? null)}</td>
                                                <td>
                                                    <span className={styles.sub}>{routeLine(r, ai.models)}</span>
                                                    <div className={styles.sub}>
                                                        prompt {r.prompt_version} · schema {r.schema_version} · max{' '}
                                                        {fmt(r.max_output_tokens)} out
                                                        {Number(r.cache_identical_inputs) ? ' · cache on' : ''}
                                                        {Number(r.active) ? '' : ' · INACTIVE'}
                                                    </div>
                                                </td>
                                                <td className={tl.right}>
                                                    {t
                                                        ? `${fmt(t.calls)}${Number(t.calls_24h) ? ` (${fmt(t.calls_24h)} in 24h)` : ''}`
                                                        : '0'}
                                                </td>
                                                <td className={tl.right}>{t ? fmt(t.subjects) : '0'}</td>
                                                <td className={tl.right}>
                                                    {t ? `${fmtTok(t.input_tokens)} / ${fmtTok(t.output_tokens)}` : '—'}
                                                </td>
                                                <td className={tl.right}>{t ? penceStr(t.cost_pence_24h) : '—'}</td>
                                                <td className={tl.right}>{t ? penceStr(t.cost_pence) : '—'}</td>
                                                <td>
                                                    <span className={styles.sub}>
                                                        {t?.last_call_at ? ago(t.last_call_at, now) : 'never'}
                                                    </span>
                                                </td>
                                            </tr>
                                        )
                                    })}
                                    {ai.textract && (
                                        <tr>
                                            <td>
                                                <span className={tl.mono}>textract.ocr</span>
                                                <div className={styles.sub}>
                                                    AWS Textract, not an LLM: OCR for scanned PDFs and images
                                                </div>
                                            </td>
                                            <td>{stageTitle('preprocess')}</td>
                                            <td>
                                                <span className={styles.sub}>
                                                    DetectDocumentText · pdf.js first, Textract only when a PDF has
                                                    little text or the attachment is an image
                                                </span>
                                            </td>
                                            <td
                                                className={tl.right}
                                            >{`${fmt(ai.textract.attachments)}${Number(ai.textract.attachments_24h) ? ` (${fmt(ai.textract.attachments_24h)} in 24h)` : ''}`}</td>
                                            <td className={tl.right}>—</td>
                                            <td className={tl.right}>{fmt(ai.textract.pages)} pages</td>
                                            <td className={tl.right}>≈ {penceStr(ai.textract.est_pence_24h)}</td>
                                            <td className={tl.right}>≈ {penceStr(ai.textract.est_pence)}</td>
                                            <td />
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        {ai.byModel.length > 0 && (
                            <>
                                <div className={styles.sub} style={{ marginTop: 12 }}>
                                    <strong>By model</strong> — one row per task × model × role. "cached" means an
                                    identical input was answered from the ledger at no cost.
                                </div>
                                <div className={styles.tableWrap}>
                                    <table className={styles.table}>
                                        <thead>
                                            <tr>
                                                <th>Model</th>
                                                <th>Task</th>
                                                <th>Role</th>
                                                <th className={tl.right}>Calls</th>
                                                <th className={tl.right}>ok / cached / failed</th>
                                                <th className={tl.right}>Tokens in / out</th>
                                                <th className={tl.right}>Spend 24h</th>
                                                <th className={tl.right}>Spend all</th>
                                                <th className={tl.right}>Avg latency</th>
                                                <th>Last call</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {ai.byModel.map((r) => (
                                                <tr key={`${r.task}|${r.model_key}|${r.role}`}>
                                                    <td>
                                                        <div className={styles.cellStack}>
                                                            <strong>{modelLabel(r.model_key, ai.models)}</strong>
                                                            <span className={[styles.sub, tl.mono].join(' ')}>
                                                                {r.model_id}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className={tl.mono}>{r.task}</span>
                                                        <div className={styles.sub}>{stageTitle(r.stage)}</div>
                                                    </td>
                                                    <td>{r.role}</td>
                                                    <td className={tl.right}>{fmt(r.calls)}</td>
                                                    <td className={tl.right}>
                                                        {fmt(r.ok)} / {fmt(r.cached)} /{' '}
                                                        {Number(r.failed) ? (
                                                            <span className={styles.error}>{fmt(r.failed)}</span>
                                                        ) : (
                                                            '0'
                                                        )}
                                                    </td>
                                                    <td className={tl.right}>
                                                        {fmtTok(r.input_tokens)} / {fmtTok(r.output_tokens)}
                                                    </td>
                                                    <td className={tl.right}>{penceStr(r.cost_pence_24h)}</td>
                                                    <td className={tl.right}>{penceStr(r.cost_pence)}</td>
                                                    <td className={tl.right}>
                                                        {r.avg_latency_ms == null ? '—' : `${fmt(r.avg_latency_ms)} ms`}
                                                    </td>
                                                    <td>
                                                        <span className={styles.sub}>
                                                            {r.last_call_at ? ago(r.last_call_at, now) : '—'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}
                        {otherTasks.length > 0 && (
                            <div className={styles.sub} style={{ marginTop: 8 }}>
                                Also in the ledger but outside this pipeline:{' '}
                                {otherTasks
                                    .map((t) => `${t.task} (${fmt(t.calls)} calls, ${penceStr(t.cost_pence)})`)
                                    .join(' · ')}{' '}
                                — address lookup for LOC sending, form builder, legacy funding monitor.
                            </div>
                        )}
                        <div className={styles.sub} style={{ marginTop: 8 }}>
                            Prices per model are on the Models page. Tokens are the providers' reported usage; spend is
                            computed from those tokens at the model's price list when the call was made, converted at
                            the rate shown. Textract has no ledger row, so its figure is pages × $1.50/1,000 (eu-west-2
                            list price).
                        </div>
                    </>
                )}
            </Card>
        )
    }

    const runButton = () => {
        const disabled = Boolean(busy)
        if (isRunning)
            return (
                <Button
                    size="sm"
                    variant="danger"
                    onClick={() => stop(stage.name)}
                    disabled={disabled || job?.state === 'stopping'}
                    loading={busy === `Stop ${stage.name}`}
                >
                    Stop
                </Button>
            )
        if (stage.name === 'extract')
            return (
                <Button
                    size="sm"
                    variant="primary"
                    onClick={startExtract}
                    disabled={disabled || !q}
                    loading={busy === 'Run extract'}
                >
                    Run extraction
                </Button>
            )
        if (stage.name === 'summarise')
            return (
                <Button
                    size="sm"
                    variant="primary"
                    onClick={startSummarise}
                    disabled={disabled || !q}
                    loading={busy === 'Run summarise'}
                >
                    Summarise threads
                </Button>
            )
        if (stage.name === 'sweep')
            return (
                <Button
                    size="sm"
                    variant="primary"
                    onClick={() => start('sweep')}
                    disabled={disabled || scopeIsLive}
                    loading={busy === 'Run sweep'}
                    title={scopeIsLive ? 'This mailbox is live: mail arrives through the SES webhook, there is nothing to sweep.' : undefined}
                >
                    {scopeIsLive ? 'Nothing to sweep (live)' : `Run sweep${scope ? ' (this mailbox)' : ''}`}
                </Button>
            )
        return (
            <Button
                size="sm"
                variant="primary"
                onClick={() => start(stage.name)}
                disabled={disabled}
                loading={busy === `Run ${stage.name}`}
            >
                Run {stage.title.toLowerCase()}
            </Button>
        )
    }

    const KV = ({ items }: { items: { label: string; value: string | number }[] }) => (
        <div className={tl.kv}>
            {items.map((i) => (
                <div key={i.label} className={tl.kvItem}>
                    <b>{i.value}</b>
                    <span>{i.label}</span>
                </div>
            ))}
        </div>
    )

    const stageBody = () => {
        if (!d || !q) return null
        switch (stage.name) {
            case 'sweep': {
                const rows = d.mailboxes.filter((m) => m.enabled && (!scope || m.address === scope))
                const swept = rows.filter((m) => !m.live)
                return (
                    <>
                        <KV
                            items={[
                                { label: 'mailboxes swept', value: `${fmt(swept.length)}${rows.length > swept.length ? ` + ${fmt(rows.length - swept.length)} live` : ''}` },
                                {
                                    label: 'folders included',
                                    value: fmt(rows.reduce((a, m) => a + Number(m.included_folders), 0)),
                                },
                                { label: 'unique emails in scope', value: fmt(q.sweep_total) },
                                {
                                    label: 'downloaded',
                                    value: `${fmt(q.sweep_fetched)} (${pct(Number(q.sweep_fetched), Number(q.sweep_total))}%)`,
                                },
                                { label: 'still to download', value: fmt(q.items_to_sweep) },
                                {
                                    label: 'stored as new email (all runs)',
                                    value: fmt(rows.reduce((a, m) => a + Number(m.ingested_count), 0)),
                                },
                                { label: 'folders still backfilling', value: fmt(q.folders_to_sweep) },
                                {
                                    label: 'folders on live delta',
                                    value: fmt(rows.reduce((a, m) => a + Number(m.delta_folders), 0)),
                                },
                            ]}
                        />
                        <ProgressBar
                            value={Number(q.sweep_fetched)}
                            max={Math.max(1, Number(q.sweep_total))}
                            tone={q.folders_to_sweep ? 'accent' : 'good'}
                            thin
                            label="Unique emails downloaded. An email filed in several folders counts once; the same figures as the Mailboxes page."
                        />
                        <div className={styles.tableWrap}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>Mailbox</th>
                                        <th className={tl.right}>Folders (inc.)</th>
                                        <th className={tl.right}>Emails</th>
                                        <th className={tl.right}>Downloaded</th>
                                        <th className={tl.right}>To download</th>
                                        <th className={tl.right}>Linked</th>
                                        <th className={tl.right}>Errors</th>
                                        <th>Folders</th>
                                        <th>Last synced</th>
                                        <th />
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((m) =>
                                        m.live ? (
                                            <tr key={m.address}>
                                                <td>
                                                    <div className={styles.cellStack}>
                                                        <strong>
                                                            {m.display_name || m.address}{' '}
                                                            <StatusPill tone="good" size="sm" dot>
                                                                Live (webhook)
                                                            </StatusPill>
                                                        </strong>
                                                        <span className={styles.sub}>{m.address}</span>
                                                    </div>
                                                </td>
                                                <td colSpan={8}>
                                                    <span className={styles.sub}>
                                                        Not swept: replies to our letters arrive through Amazon SES and are
                                                        recorded the moment they land, so there are no folders, counts or
                                                        re-sync here. Preprocess, extraction and reconcile still apply to
                                                        this mail; pick it in the scope selector to work through it alone.
                                                    </span>
                                                </td>
                                                <td />
                                            </tr>
                                        ) : (
                                        <tr key={m.address}>
                                            <td>
                                                <div className={styles.cellStack}>
                                                    <strong>{m.display_name || m.address}</strong>
                                                    <span className={styles.sub}>{m.address}</span>
                                                </div>
                                            </td>
                                            <td className={tl.right}>
                                                {fmt(m.included_folders)} / {fmt(m.folder_count)}
                                            </td>
                                            <td className={tl.right}>{fmt(m.estimated_total)}</td>
                                            <td className={tl.right}>
                                                <span className={styles.pct}>
                                                    {pct(Number(m.stored_count), Number(m.estimated_total))}%
                                                </span>{' '}
                                                <span className={styles.sub}>{fmt(m.stored_count)}</span>
                                            </td>
                                            <td className={tl.right}>
                                                {Number(m.missing_count) ? (
                                                    <span className={styles.error}>{fmt(m.missing_count)}</span>
                                                ) : (
                                                    '0'
                                                )}
                                            </td>
                                            <td className={tl.right}>{fmt(m.linked_count)}</td>
                                            <td className={tl.right}>
                                                {Number(m.error_count) ? (
                                                    <span className={styles.error}>{fmt(m.error_count)}</span>
                                                ) : (
                                                    '0'
                                                )}
                                            </td>
                                            <td>
                                                <span className={styles.sub}>
                                                    {fmt(m.backfilling_folders)} backfilling · {fmt(m.delta_folders)}{' '}
                                                    live
                                                    {Number(m.failed_folders)
                                                        ? ` · ${fmt(m.failed_folders)} failed`
                                                        : ''}
                                                    {Number(m.paused_folders)
                                                        ? ` · ${fmt(m.paused_folders)} paused`
                                                        : ''}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={styles.sub}>
                                                    {m.last_synced_at ? ago(m.last_synced_at, now) : 'never'}
                                                </span>
                                            </td>
                                            <td className={tl.right}>
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() => resync(m)}
                                                    disabled={
                                                        Boolean(busy) || (jobFor('sweep')?.state ?? 'idle') !== 'idle'
                                                    }
                                                    loading={busy === `Re-sync ${m.address}`}
                                                >
                                                    Re-sync
                                                </Button>
                                            </td>
                                        </tr>
                                        )
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <span className={styles.sub}>
                            {scope
                                ? `Run sweep only fetches ${scope}. `
                                : 'Run sweep fetches every enabled mailbox; pick one in the scope selector above to go mailbox by mailbox. '}
                            Include or exclude folders on the Mailboxes page before sweeping. "Held" counts distinct
                            messages we actually store per folder against the totals Graph reports; if a mailbox shows
                            Missing after its folders are all live, Re-sync walks them again and picks up only what is
                            absent.
                        </span>
                        <Banner tone="info" title="Running the sweep faster">
                            Only one dashboard sweep runs at a time (it holds a job lock), but each folder is also locked
                            separately, so extra machines can sweep in parallel from the command line with{' '}
                            <code>node -r dotenv/config scripts/comms/ingest-mailboxes.js --loop --mailbox=&lt;address&gt;</code>
                            . Give each process a different mailbox: Microsoft limits requests per mailbox, so two processes
                            on the same one only throttle each other, while different mailboxes scale roughly linearly.
                            Folders another process holds are skipped, a stalled machine's folders free up after five
                            minutes, and progress on this page and the Mailboxes page stays accurate because it is read
                            from the database. Until the message-id uniqueness migration is applied, the same email
                            arriving in two mailboxes at the same moment can be stored twice.
                        </Banner>
                    </>
                )
            }
            case 'preprocess': {
                const byState = Object.fromEntries(d.preprocess.byState.map((r) => [r.state, Number(r.total)]))
                const total = Object.values(byState).reduce((a, b) => a + b, 0)
                return (
                    <>
                        <KV
                            items={[
                                { label: 'emails in the database', value: fmt(total) },
                                {
                                    label: 'ready (preprocessed)',
                                    value: `${fmt(byState.ready || 0)} (${pct(byState.ready || 0, total)}%)`,
                                },
                                { label: 'still pending', value: fmt(byState.pending || 0) },
                                { label: 'partial (attachments left)', value: fmt(byState.partial || 0) },
                                { label: 'failed', value: fmt(byState.failed || 0) },
                            ]}
                        />
                        <ProgressBar
                            value={byState.ready || 0}
                            max={Math.max(1, total)}
                            tone={q.preprocess_pending ? 'accent' : 'good'}
                            thin
                            label="Emails with clean text ready for matching and extraction"
                        />
                        <div className={styles.tableWrap}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>Source mailbox</th>
                                        <th className={tl.right}>Emails</th>
                                        <th className={tl.right}>Ready</th>
                                        <th className={tl.right}>Outstanding</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {d.preprocess.byMailbox.map((r) => (
                                        <tr key={r.mailbox}>
                                            <td>{r.mailbox || '—'}</td>
                                            <td className={tl.right}>{fmt(r.total)}</td>
                                            <td className={tl.right}>{fmt(r.ready)}</td>
                                            <td className={tl.right}>
                                                {Number(r.outstanding) ? <strong>{fmt(r.outstanding)}</strong> : '0'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {d.preprocess.recentErrors.length > 0 && (
                            <Banner tone="warn" title="Recent preprocess failures">
                                {d.preprocess.recentErrors.map((e) => (
                                    <div key={e.id} className={styles.sub}>
                                        {e.subject || '(no subject)'} — {e.preprocess_error}
                                    </div>
                                ))}
                            </Banner>
                        )}
                        <span className={styles.sub}>
                            Each run takes batches of 50 (4 in parallel) until nothing is pending. Rows that fail are
                            skipped for the rest of the run and retried on the next one.
                        </span>
                    </>
                )
            }
            case 'identity': {
                const c = d.identity
                return (
                    <>
                        <KV
                            items={[
                                { label: 'LOC sends', value: fmt(c.sends.total) },
                                { label: 'sends without a case', value: fmt(c.sends.without_case) },
                                {
                                    label: 'cases',
                                    value: `${fmt(c.cases.total)}${Number(c.cases.backfilled) ? ` (${fmt(c.cases.backfilled)} backfilled)` : ''}`,
                                },
                                { label: 'claims with sends', value: fmt(c.coverage.claims_with_sends) },
                                {
                                    label: 'claims with identity snapshot',
                                    value: `${fmt(c.coverage.claims_with_identity)} (${pct(Number(c.coverage.claims_with_identity), Number(c.coverage.claims_with_sends))}%)`,
                                },
                                { label: 'snapshot errors', value: fmt(c.coverage.identity_errors) },
                            ]}
                        />
                        <ProgressBar
                            value={Number(c.coverage.claims_with_identity)}
                            max={Math.max(1, Number(c.coverage.claims_with_sends))}
                            tone={q.claims_without_identity ? 'accent' : 'good'}
                            thin
                            label="Claims with an identity snapshot"
                        />
                        {c.casesByStatus.length > 0 && (
                            <div className={styles.counts}>
                                {c.casesByStatus.map((r) => (
                                    <span key={r.status}>
                                        <strong>{fmt(r.total)}</strong> {r.status.replace(/_/g, ' ')}
                                    </span>
                                ))}
                            </div>
                        )}
                        <span className={styles.sub}>
                            {scope
                                ? 'Case identity is keyed on claims, not mailboxes, so this stage is always global. '
                                : ''}
                            A run first creates cases for any send that has none (idempotent), then snapshots identities
                            in bulk: 500 claims per batch, one query per introducer database plus one on the main
                            database, written back in a single upsert. Snapshots that fail are recorded with the error
                            and not retried automatically.
                        </span>
                    </>
                )
            }
            case 'extract': {
                const e = d.extraction
                const byState = Object.fromEntries(e.byState.map((r) => [r.state, Number(r.total)]))
                const total = Object.values(byState).reduce((a, b) => a + b, 0)
                const linked = e.byLink.filter((r) => r.state === 'linked').reduce((a, r) => a + Number(r.total), 0)
                return (
                    <>
                        {status?.autoExtract && (
                            <Banner
                                tone={status.autoExtract.enabled ? 'good' : 'info'}
                                title={`Automatic extraction is ${status.autoExtract.enabled ? 'ON' : 'OFF'}`}
                            >
                                {status.autoExtract.enabled
                                    ? `Source: ${status.autoExtract.source}. New email is extracted by the cloud reconcile pass as it arrives.`
                                    : `Source: ${status.autoExtract.source}. The cloud passes sweep, preprocess and match but do not spend on extraction until you set llmExtractEnabled to true in Secrets Manager (${status.autoExtract.secretName}). Runs started here always extract.`}
                            </Banner>
                        )}
                        {!isRunning && (
                            <div className={styles.inlineForm}>
                                <div className={styles.cellStack}>
                                    <strong>Settings for "Run extraction"</strong>
                                    <span className={styles.sub}>
                                        Set these before pressing the button above. They apply to this run only; the cloud
                                        pass uses the routed defaults.
                                    </span>
                                </div>
                                <div className={styles.formFooter} style={{ marginTop: 0 }}>
                                    <Field label="Max emails this run" hint="Blank = everything pending" inline>
                                        <Input
                                            className={styles.narrow}
                                            value={extractParams.maxItems}
                                            onChange={(ev) =>
                                                setExtractParams((p) => ({
                                                    ...p,
                                                    maxItems: ev.target.value.replace(/[^\d]/g, ''),
                                                }))
                                            }
                                            small
                                        />
                                    </Field>
                                    <Field label="Stop at £" hint="Optional spend cap" inline>
                                        <Input
                                            className={styles.narrow}
                                            value={extractParams.maxCostPounds}
                                            onChange={(ev) =>
                                                setExtractParams((p) => ({
                                                    ...p,
                                                    maxCostPounds: ev.target.value.replace(/[^\d.]/g, ''),
                                                }))
                                            }
                                            small
                                        />
                                    </Field>
                                    <Field label="Voting" hint="Blank = as routed on AI Models" inline>
                                        <NativeSelect
                                            small
                                            value={extractParams.voting}
                                            onChange={(ev) =>
                                                setExtractParams((p) => ({
                                                    ...p,
                                                    voting: ev.target.value as ExtractParams['voting'],
                                                }))
                                            }
                                            options={[
                                                { value: '', label: 'Routed' },
                                                { value: 'single', label: 'Single model (cheapest)' },
                                                { value: 'pair', label: 'Pair (two models agree)' },
                                                { value: 'pair_plus_tiebreak', label: 'Pair + tie-break' },
                                            ]}
                                        />
                                    </Field>
                                    <Field label="Include" inline>
                                        <NativeSelect
                                            small
                                            value={
                                                extractParams.states.includes('failed') ? 'pending,failed' : 'pending'
                                            }
                                            onChange={(ev) =>
                                                setExtractParams((p) => ({ ...p, states: ev.target.value.split(',') }))
                                            }
                                            options={[
                                                { value: 'pending,failed', label: 'Pending + previously failed' },
                                                { value: 'pending', label: 'Pending only' },
                                            ]}
                                        />
                                    </Field>
                                    <Field label="Parallel" hint="Emails in flight at once" inline>
                                        <NativeSelect
                                            small
                                            value={String(extractParams.concurrency)}
                                            onChange={(ev) =>
                                                setExtractParams((p) => ({
                                                    ...p,
                                                    concurrency: Number(ev.target.value),
                                                }))
                                            }
                                            options={[1, 2, 4, 8, 16, 24, 32].map((n) => ({
                                                value: String(n),
                                                label: n >= 16 ? `${n} (backlog)` : String(n),
                                            }))}
                                        />
                                    </Field>
                                </div>
                                <span className={styles.sub}>
                                    Guide: about {pounds(EST_PENCE_PER_EMAIL)} per email on pair voting. Start with a
                                    small cap, check the Inbox and AI Models pages, then raise it. Speed scales with
                                    Parallel — the work is waiting on the models, not the server — and if a provider
                                    rate-limits us every worker pauses together and retries, so 16–32 is safe for
                                    clearing a backlog. The instructions and schema sent with every email are cached by
                                    the providers, so repeated calls cost less than the first.
                                </span>
                            </div>
                        )}
                        <KV
                            items={[
                                { label: 'emails in scope', value: fmt(total) },
                                {
                                    label: 'extracted',
                                    value: `${fmt(byState.done || 0)} (${pct(byState.done || 0, total)}%)`,
                                },
                                { label: 'pending', value: fmt(byState.pending || 0) },
                                { label: 'failed', value: fmt(byState.failed || 0) },
                                { label: 'skipped (noise / rules)', value: fmt(byState.skipped || 0) },
                                { label: 'linked to a case', value: fmt(linked) },
                                { label: 'disputed by the models', value: fmt(e.disputed.total) },
                                { label: 'AI spend last 24h', value: pounds(e.cost.day_pence) },
                                { label: 'AI spend last 30 days', value: pounds(e.cost.month_pence) },
                                {
                                    label: 'AI spend all time',
                                    value: `${pounds(e.cost.all_pence)} · ${fmt(e.cost.calls)} calls`,
                                },
                            ]}
                        />
                        <ProgressBar
                            value={byState.done || 0}
                            max={Math.max(1, total)}
                            tone={q.extraction_pending ? 'accent' : 'good'}
                            thin
                            label="Emails extracted and categorised"
                        />
                        <div className={styles.twoCol}>
                            <div className={styles.tableWrap}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>Category (current extraction)</th>
                                            <th className={tl.right}>Emails</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {e.byCategory.length === 0 && (
                                            <tr>
                                                <td colSpan={2} className={styles.sub}>
                                                    Nothing extracted yet.
                                                </td>
                                            </tr>
                                        )}
                                        {e.byCategory.map((r) => (
                                            <tr key={r.category}>
                                                <td>{r.category.replace(/_/g, ' ')}</td>
                                                <td className={tl.right}>{fmt(r.total)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className={styles.tableWrap}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>Link state</th>
                                            <th>What it means</th>
                                            <th className={tl.right}>Emails</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {linkStateGroups(e.byLink).map((g) => (
                                            <Fragment key={g.state}>
                                                <tr>
                                                    <td>
                                                        <strong>{linkStateLabel(g.state)}</strong>
                                                    </td>
                                                    <td className={styles.sub}>{LINK_STATE_MEANING[g.state] ?? ''}</td>
                                                    <td className={tl.right}>
                                                        <strong>{fmt(g.total)}</strong>
                                                    </td>
                                                </tr>
                                                {g.reasons.map((r) => (
                                                    <tr key={`${g.state}-${r.reason}`}>
                                                        <td className={styles.sub} style={{ paddingLeft: 24 }}>
                                                            {unlinkedReasonLabel(r.reason)}
                                                        </td>
                                                        <td className={styles.sub}>{describeUnlinkedReason(r.reason)}</td>
                                                        <td className={`${tl.right} ${styles.sub}`}>{fmt(r.total)}</td>
                                                    </tr>
                                                ))}
                                            </Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <span className={styles.sub}>{LINKING_EXPLAINER}</span>
                    </>
                )
            }
            case 'reconcile': {
                const r = d.reconcile
                const report = job?.progress?.report
                const steps = report
                    ? Object.entries(report).filter(([, v]) => v && typeof v === 'object' && 'ms' in (v as any))
                    : []
                return (
                    <>
                        <KV
                            items={[
                                { label: 'unlinked emails (need a match or a human)', value: fmt(q.unlinked) },
                                { label: 'with candidate matches', value: fmt(q.with_candidates) },
                                {
                                    label: 'pending conversations awaiting reply',
                                    value: fmt(r.pendingConversations?.total ?? 0),
                                },
                                { label: 'of which expired', value: fmt(r.pendingConversations?.expired ?? 0) },
                                {
                                    label: 'last cloud tick',
                                    value: lastCron('reconcile')
                                        ? ago(lastCron('reconcile')!.finished_at, now)
                                        : 'never',
                                },
                            ]}
                        />
                        {r.unlinked.length > 0 && (
                            <div className={styles.tableWrap}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>Why not linked</th>
                                            <th>What that means</th>
                                            <th className={tl.right}>Emails</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {r.unlinked.map((u) => (
                                            <tr key={u.reason}>
                                                <td>{unlinkedReasonLabel(u.reason)}</td>
                                                <td className={styles.sub}>{describeUnlinkedReason(u.reason)}</td>
                                                <td className={tl.right}>{fmt(u.total)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        <span className={styles.sub}>
                            Reconcile re-runs matching for every not-linked email whenever new client snapshots or lender references
                            appear, so this count should fall on its own. Whatever remains is linked by hand from the Inbox tab
                            "Not linked to a case".
                        </span>
                        {steps.length > 0 && (
                            <div className={styles.tableWrap}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>Step (last run)</th>
                                            <th>Result</th>
                                            <th className={tl.right}>Time</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {steps.map(([k, v]: [string, any]) => (
                                            <tr key={k}>
                                                <td>{k}</td>
                                                <td className={styles.sub}>
                                                    {v.skipped
                                                        ? `skipped — ${v.skipped}`
                                                        : Object.entries(v)
                                                              .filter(([kk]) => kk !== 'ms')
                                                              .map(
                                                                  ([kk, vv]) =>
                                                                      `${kk} ${typeof vv === 'object' ? JSON.stringify(vv) : vv}`,
                                                              )
                                                              .join(' · ')}
                                                </td>
                                                <td className={tl.right}>{fmt(v.ms)} ms</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        <span className={styles.sub}>
                            A reconcile pass is what the cloud runs every {RECONCILE_INTERVAL_MIN} minutes. It stays
                            within a 4-minute budget and skips any stage you are running from this page.
                            {scope
                                ? ' The counts above are for the selected mailbox; the pass itself always covers everything.'
                                : ''}
                        </span>
                    </>
                )
            }
            case 'summarise': {
                const sm = d.summarise
                const c = sm?.coverage ?? {}
                return (
                    <>
                        {status?.threadSummary && (
                            <Banner
                                tone={status.threadSummary.enabled ? 'good' : 'info'}
                                title={`Thread summaries are ${status.threadSummary.enabled ? 'ON' : 'OFF'}`}
                            >
                                {status.threadSummary.enabled
                                    ? `Source: ${status.threadSummary.source}. The comms worker refreshes summaries every couple of minutes for threads with new activity.`
                                    : `Source: ${status.threadSummary.source}. Nothing is summarised until threadSummaryEnabled is set to true in Secrets Manager (${status.threadSummary.secretName}). Unlike extraction, a run started here respects the switch too, so there is no way to spend on summaries by accident.`}
                            </Banner>
                        )}
                        {sm?.error && (
                            <Banner tone="bad" title="Could not read summary coverage">
                                {sm.error}
                            </Banner>
                        )}
                        {!isRunning && (
                            <div className={styles.inlineForm}>
                                <div className={styles.cellStack}>
                                    <strong>Settings for "Summarise threads"</strong>
                                    <span className={styles.sub}>
                                        Newest activity first. They apply to this run only; the worker loop uses the
                                        routed defaults.
                                    </span>
                                </div>
                                <div className={styles.formFooter} style={{ marginTop: 0 }}>
                                    <Field label="Max threads this run" hint="Blank = every thread that is due" inline>
                                        <Input
                                            className={styles.narrow}
                                            value={summariseParams.maxItems}
                                            onChange={(ev) =>
                                                setSummariseParams((p) => ({ ...p, maxItems: ev.target.value }))
                                            }
                                            inputMode="numeric"
                                        />
                                    </Field>
                                    <Field label="Stop at £" hint="Blank = no cost cap" inline>
                                        <Input
                                            className={styles.narrow}
                                            value={summariseParams.maxCostPounds}
                                            onChange={(ev) =>
                                                setSummariseParams((p) => ({ ...p, maxCostPounds: ev.target.value }))
                                            }
                                            inputMode="decimal"
                                        />
                                    </Field>
                                </div>
                            </div>
                        )}
                        <KV
                            items={[
                                { label: 'threads awaiting a summary', value: fmt(c.due ?? q.summarise_pending ?? 0) },
                                { label: 'of which have an out-of-date one', value: fmt(c.stale ?? 0) },
                                { label: 'threads summarised', value: fmt(c.summarised ?? 0) },
                                { label: 'active threads', value: fmt(c.active ?? 0) },
                                { label: 'single-message threads (not summarised)', value: fmt(c.single_message ?? 0) },
                                { label: 'last summary written', value: c.last_summary_at ? ago(c.last_summary_at, now) : 'never' },
                            ]}
                        />
                        {(sm?.recent?.length ?? 0) > 0 && (
                            <div className={styles.tableWrap}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>Most recent summaries</th>
                                            <th>Counterparty</th>
                                            <th>Suggested next action</th>
                                            <th className={tl.right}>Messages</th>
                                            <th className={tl.right}>When</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sm!.recent.map((r) => (
                                            <tr key={r.id}>
                                                <td>
                                                    {r.mailbox ? (
                                                        <Link
                                                            to={`/comms/mailboxes/${encodeURIComponent(r.mailbox)}?tab=received&conversation=${r.id}`}
                                                        >
                                                            {r.subject || '(no subject)'}
                                                        </Link>
                                                    ) : (
                                                        r.subject || '(no subject)'
                                                    )}
                                                    {r.claim_ref && <div className={styles.sub}>{r.claim_ref}</div>}
                                                </td>
                                                <td className={styles.sub}>{r.counterparty_address || '—'}</td>
                                                <td className={styles.sub}>{r.suggested_next_action || '—'}</td>
                                                <td className={tl.right}>{fmt(r.message_count)}</td>
                                                <td className={`${tl.right} ${styles.sub}`}>{ago(r.summary_at, now)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        <span className={styles.sub}>
                            A thread becomes due again whenever a message lands after its last summary. Threads that
                            are only our unanswered letter are skipped, as are archived ones. The model writes the
                            summary and a suggested next action only; who the thread is waiting on stays derived from
                            the last message.
                        </span>
                    </>
                )
            }
            case 'book': {
                const b = d.book
                const sources = b?.sources ?? []
                const failing = sources.filter((s) => s.last_error)
                return (
                    <>
                        {b?.error && <Banner tone="warn">Could not read the book sync state: {b.error}</Banner>}
                        <KV
                            items={[
                                { label: 'claims in the index', value: fmt(b?.identity?.rows_total) },
                                { label: 'copied from the book', value: fmt(b?.identity?.from_book) },
                                { label: 'sources', value: fmt(sources.length) },
                                { label: 'sources failing', value: fmt(failing.length) },
                                { label: 'last copy', value: b?.identity?.last_snapshot_at ? ago(b.identity.last_snapshot_at, now) : 'never' },
                            ]}
                        />
                        {sources.length > 0 && (
                            <div className={styles.tableWrap}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>Source</th>
                                            <th>Shape</th>
                                            <th>Copies changes since</th>
                                            <th>Last pass</th>
                                            <th className={tl.right}>Rows read</th>
                                            <th className={tl.right}>Claims copied</th>
                                            <th>Problem</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sources.map((s) => (
                                            <tr key={s.source_key}>
                                                <td>{s.source_key}</td>
                                                <td>{s.schema_family}</td>
                                                <td>{s.watermark_at ? ago(s.watermark_at, now) : 'not yet (full walk next)'}</td>
                                                <td>{s.last_run_at ? `${ago(s.last_run_at, now)} · ${fmt(s.last_run_ms)} ms` : 'never'}</td>
                                                <td className={tl.right}>{fmt(s.last_rows)}</td>
                                                <td className={tl.right}>{fmt(s.last_claims)}</td>
                                                <td className={s.last_error ? styles.error : undefined}>{s.last_error ?? '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        <span className={styles.sub}>
                            A source with no watermark is walked end to end on its next pass; after that only rows whose
                            source timestamp moved since the previous pass (with a ten-minute overlap) are copied. oaGenieSync
                            has no updated-at column, so only new OA claims are picked up incrementally.
                        </span>
                    </>
                )
            }
        }
    }

    const runs = runsFor(stage.name)

    return (
        <div className={styles.stack}>
            {paused && (
                <Banner tone="bad" title="Production pipeline stopped">
                    {pauseFromEnv
                        ? 'COMMS_PIPELINE_PAUSED is set on this API, so nothing here starts a pipeline job.'
                        : `Stopped${status?.pause?.by ? ` by ${status.pause.by}` : ''}${status?.pause?.at ? ` ${ago(status.pause.at, now)}` : ''}. No sweeping, reading or matching runs in production until it is started again${
                              settingsAllowed ? ' (Comms settings, below)' : ''
                          }. Mail still arrives and is kept.`}
                </Banner>
            )}

            {/* Scope */}
            <div className={styles.actions}>
                <Field
                    label="Scope"
                    hint={
                        scopeIsLive
                            ? 'Preprocess and extraction run for the SES stream only. It is never swept: its mail arrives by webhook. Case identity is always global.'
                            : scope
                              ? 'Sweep, preprocess and extraction run for this mailbox only. Case identity is always global.'
                              : 'Pick one mailbox to work through the pipeline on a small set first.'
                    }
                    inline
                >
                    <NativeSelect
                        small
                        value={scope}
                        onChange={(ev) => {
                            setScope(ev.target.value)
                            setSelected(null)
                        }}
                        options={[
                            { value: '', label: 'All mailboxes' },
                            ...(status?.detail.mailboxes || [])
                                .filter((m) => m.enabled)
                                .map((m) => ({
                                    value: m.address,
                                    label: m.live ? `${m.display_name || m.address} · live (webhook)` : `${m.display_name || m.address} · ${fmt(m.estimated_total)} emails`,
                                })),
                        ]}
                    />
                </Field>
                {scope && (
                    <StatusPill tone="accent" size="sm">
                        scoped to {scope}
                    </StatusPill>
                )}
            </div>

            {/* Timeline */}
            <div className={tl.legend}>
                <span>
                    <i style={{ background: '#4f7d6b' }} /> in progress
                </span>
                <span>
                    <i style={{ background: '#4a6d8c' }} /> done
                </span>
                <span>
                    <i style={{ background: '#a85454' }} /> pending
                </span>
                <span>
                    <i style={{ background: '#94a3b8' }} /> not reached
                </span>
                <span style={{ marginLeft: 'auto' }}>
                    <Button size="sm" variant="ghost" onClick={load}>
                        Refresh
                    </Button>
                </span>
            </div>
            <div className={tl.timeline} role="tablist" aria-label="Pipeline stages">
                {STAGES.map((s) => {
                    const st = stageStatus(s.name)
                    const j = jobFor(s.name)
                    const queue = queueFor(s.name)
                    const sub =
                        st === 'running'
                            ? `${j?.runningHere ? 'this server' : j?.leaseOwner?.split(':')[0]} · since ${fmtDate(j?.startedAt)}`
                            : interrupted(j)
                              ? 'interrupted · resumes on Run'
                              : j?.finishedAt
                                ? `last run ${ago(j.finishedAt, now)} · ${j.finishedReason}`
                                : 'never run'
                    return (
                        <button
                            key={s.name}
                            type="button"
                            role="tab"
                            aria-selected={s.name === selectedStage}
                            className={[tl.stage, tl[st], s.name === selectedStage ? tl.selected : ''].join(' ')}
                            onClick={() => setSelected(s.name)}
                        >
                            <span className={tl.stageStep}>
                                Step {s.step}
                                {s.ai && (
                                    <span className={[tl.aiBadge, s.ai === 'llm' ? tl.aiLlm : tl.aiOcr].join(' ')}>
                                        {s.ai === 'llm' ? 'LLM' : 'OCR'}
                                    </span>
                                )}
                            </span>
                            <span className={tl.stageTitle}>{s.title}</span>
                            <span className={tl.stageSub}>
                                <span className={tl.statusDot} />
                                {STATUS_LABEL[st]}
                            </span>
                            <span className={tl.stageMetric}>
                                {s.name === 'reconcile' && st === 'done' ? '✓' : fmt(queue.value)}
                            </span>
                            <span className={tl.stageSub}>
                                {s.name === 'reconcile' && st === 'done'
                                    ? `ran ${ago(j?.finishedAt, now)}`
                                    : queue.label}
                            </span>
                            <span className={tl.stageSub}>{sub}</span>
                        </button>
                    )
                })}
            </div>

            {/* Selected stage detail */}
            <Card
                title={
                    <span className={styles.actions}>
                        <span>
                            {stage.step}. {stage.title}
                        </span>
                        <StatusPill
                            tone={STATUS_PILL[stageStatus(stage.name)]}
                            dot={stageStatus(stage.name) === 'running'}
                            size="sm"
                        >
                            {STATUS_LABEL[stageStatus(stage.name)]}
                        </StatusPill>
                    </span>
                }
                subtitle={stage.what}
                actions={runButton()}
            >
                <div className={styles.stack}>
                    {stage.detail}
                    <div className={styles.counts}>
                        <span>Cloud: {stage.cloud}</span>
                    </div>
                    <span className={styles.sub}>{runLine(job)}</span>
                    {isRunning && stage.name !== 'sweep' && job?.progress && job.progress.remaining != null && (
                        <ProgressBar
                            value={job.progress.processed}
                            max={job.progress.processed + job.progress.remaining}
                            tone="accent"
                            thin
                            label={`${fmt(job.progress.processed)} done this run · ${fmt(job.progress.remaining)} left`}
                        />
                    )}
                    {stageBody()}

                    <Card
                        title="Run history"
                        subtitle="What happened on each run of this stage, newest first."
                        muted
                        padding="none"
                    >
                        <div className={styles.tableWrap} style={{ border: 0 }}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>Started</th>
                                        <th>By</th>
                                        <th>Where</th>
                                        <th className={tl.right}>Duration</th>
                                        <th className={tl.right}>Batches</th>
                                        <th className={tl.right}>Processed</th>
                                        <th className={tl.right}>OK</th>
                                        <th className={tl.right}>Failed</th>
                                        <th className={tl.right}>Cost</th>
                                        <th>Outcome</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {runs.length === 0 && (
                                        <tr>
                                            <td colSpan={10} className={styles.sub}>
                                                No finished runs yet.
                                            </td>
                                        </tr>
                                    )}
                                    {runs.map((r) => (
                                        <tr key={r.id}>
                                            <td>{fmtDate(r.startedAt)}</td>
                                            <td>{r.startedBy || '—'}</td>
                                            <td className={styles.sub}>{r.owner?.split(':')[0] || '—'}</td>
                                            <td className={tl.right}>{durationOf(r.startedAt, r.finishedAt)}</td>
                                            <td className={tl.right}>{fmt(r.progress?.batches)}</td>
                                            <td className={tl.right}>{fmt(r.progress?.processed)}</td>
                                            <td className={tl.right}>{fmt(r.progress?.ok)}</td>
                                            <td className={tl.right}>
                                                {Number(r.progress?.failed) ? (
                                                    <span className={styles.error}>{fmt(r.progress?.failed)}</span>
                                                ) : (
                                                    '0'
                                                )}
                                            </td>
                                            <td className={tl.right}>
                                                {r.progress?.costPence ? pounds(r.progress.costPence) : '—'}
                                            </td>
                                            <td>
                                                <div className={styles.cellStack}>
                                                    <StatusPill tone={reasonTone(r.finishedReason)} size="sm">
                                                        {r.finishedReason}
                                                    </StatusPill>
                                                    {(r.lastError || r.progress?.note) && (
                                                        <span className={styles.sub}>
                                                            {r.lastError || r.progress?.note}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Card>
                </div>
            </Card>

            {aiCard()}

            {/* Comms settings — Sentinel admins only (the API answers 403 to everyone else), collapsed by default */}
            {settingsAllowed && (
            <Card
                title="Comms settings"
                subtitle={
                    settingsOpen
                        ? 'Everything that used to need the AWS console. Switches, and the worker on/off and loop list, take effect within a minute; nothing needs restarting.'
                        : 'Switches, comms worker and automation. Sentinel admins only.'
                }
                actions={
                    <div className={styles.actions}>
                        {settingsOpen && (
                            <Button size="sm" variant="ghost" onClick={loadSettings} disabled={Boolean(busy)}>
                                Refresh
                            </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => setSettingsOpen((v) => !v)} aria-expanded={settingsOpen}>
                            {settingsOpen ? 'Hide settings' : 'Show settings'}
                        </Button>
                    </div>
                }
                padding={settingsOpen ? 'md' : 'sm'}
            >
                <div className={styles.stack}>
                {/* The kill switch: always visible, whether or not the rest of the settings are open */}
                <div className={`${tl.killSwitch} ${paused ? tl.stopped : ''}`}>
                    <div className={tl.killSwitchText}>
                        <div className={tl.settingHead}>
                            <StatusPill tone={paused ? 'bad' : 'good'} dot size="sm">
                                {paused ? 'stopped' : 'running'}
                            </StatusPill>{' '}
                            Production pipeline
                            <InfoTip text="One switch for everything: the worker loops, the cloud schedules, the Run buttons on this page and reading new mail as it arrives. Stop it to run the pipeline from your own machine (set COMMS_PIPELINE_PAUSED=0 there). Mail is still received and stored while stopped. Applies within a minute; nothing restarts." />
                        </div>
                        <span className={styles.sub}>
                            {paused
                                ? pauseFromEnv
                                    ? 'Held by COMMS_PIPELINE_PAUSED on this API; the switch cannot change it from here.'
                                    : `Stopped${status?.pause?.by ? ` by ${status.pause.by}` : ''}${status?.pause?.at ? ` ${ago(status.pause.at, now)}` : ''}. Nothing in production is sweeping, reading or matching.`
                                : 'Everything runs as configured below.'}
                        </span>
                        <span className={tl.mono}>pipelinePaused</span>
                    </div>
                    <Button
                        size="sm"
                        variant={paused ? 'primary' : 'danger'}
                        onClick={togglePause}
                        disabled={Boolean(busy) || pauseFromEnv}
                    >
                        {paused ? 'Start pipeline' : 'Stop pipeline'}
                    </Button>
                </div>

                {settingsOpen && (
                <div className={styles.stack}>
                    {settingsError && <Banner tone="warn">{settingsError}</Banner>}

                    {/* Switches */}
                    <Card
                        muted
                        title="Switches"
                        subtitle={`Held in ${settings?.groups.runtime.secretName ?? 'comms-settings'}. Each one is live as soon as it is saved.`}
                    >
                        {settings?.groups.runtime.error && <Banner tone="warn">Could not read the settings secret: {settings.groups.runtime.error}</Banner>}
                        {settings && (
                            <div className={tl.schedules}>
                                {(
                                    [
                                        {
                                            key: 'llmExtractEnabled',
                                            label: 'Categorise new mail on arrival',
                                            what: 'Each new email is read by the model as it arrives (rules first, then the model). Off = mail waits for the Extract stage or the reconcile loop.',
                                        },
                                        {
                                            key: 'threadSummaryEnabled',
                                            label: 'Thread summaries',
                                            what: 'The Summarise stage and the worker summarise loop may write conversation summaries. Costs one model call per thread with new mail.',
                                        },
                                    ] as { key: 'llmExtractEnabled' | 'threadSummaryEnabled'; label: string; what: string }[]
                                ).map((sw) => {
                                    const env = settings.groups.runtime.envOverrides[sw.key]
                                    const on = Boolean(env ? settings.groups.runtime.effective?.[sw.key] : settings.groups.runtime.values[sw.key])
                                    return (
                                        <div key={sw.key} className={tl.setting}>
                                            <div className={tl.settingHead}>
                                                <StatusPill tone={on ? 'good' : 'neutral'} dot size="sm">
                                                    {on ? 'on' : 'off'}
                                                </StatusPill>{' '}
                                                {sw.label}
                                            </div>
                                            <span className={styles.sub}>{sw.what}</span>
                                            <div className={tl.settingControl}>
                                                <Switch
                                                    label={on ? 'On' : 'Off'}
                                                    checked={on}
                                                    disabled={Boolean(busy) || Boolean(env)}
                                                    onChange={(ev) => saveSettings('runtime', { [sw.key]: ev.target.checked }, sw.label)}
                                                />
                                            </div>
                                            <span className={tl.mono}>{sw.key}</span>
                                            {env && <span className={tl.tiny}>overridden by {env} on this API</span>}
                                        </div>
                                    )
                                })}
                                <div className={tl.setting}>
                                    <div className={tl.settingHead}>
                                        <StatusPill tone={effectiveRunner(settings) === 'worker' ? 'accent' : 'neutral'} dot size="sm">
                                            {effectiveRunner(settings)}
                                        </StatusPill>{' '}
                                        Who runs the pipeline
                                    </div>
                                    <span className={styles.sub}>
                                        <strong>worker</strong> — the action server runs every stage 24/7 on its own loops.
                                    </span>
                                    <span className={styles.sub}>
                                        <strong>api</strong> — only the Automation schedules below run, inside the case-delivery API.
                                    </span>
                                    <div className={tl.settingControl}>
                                        <NativeSelect
                                            small
                                            value={effectiveRunner(settings)}
                                            disabled={Boolean(busy) || Boolean(settings.groups.runtime.envOverrides.pipelineRunner)}
                                            onChange={(ev) => saveSettings('runtime', { pipelineRunner: ev.target.value }, 'Pipeline runner')}
                                            options={[
                                                { value: 'worker', label: 'worker — action server, 24/7' },
                                                { value: 'api', label: 'api — case-delivery API, on the schedules only' },
                                            ]}
                                        />
                                    </div>
                                    <span className={tl.mono}>pipelineRunner</span>
                                    {settings.groups.runtime.envOverrides.pipelineRunner && (
                                        <span className={tl.tiny}>overridden by {settings.groups.runtime.envOverrides.pipelineRunner} on this API</span>
                                    )}
                                </div>
                            </div>
                        )}
                    </Card>

                {/* Comms worker — shown while the runner is worker; Automation takes its place while it is api. */}
                {(!settings || effectiveRunner(settings) === 'worker') && (
                <Card
                    muted
                    title="Comms worker"
                    subtitle="The 24/7 process on sentinel-action-server that sweeps the mailboxes and runs the stages. To stop it for a while, switch Loops off (takes effect within a minute, no restart)."
                    actions={
                        <StatusPill tone={status?.worker?.runner === 'worker' ? 'accent' : 'neutral'} dot>
                            runner: {status?.worker?.runner ?? status?.runner?.value ?? '—'}
                        </StatusPill>
                    }
                >
                    {status?.worker?.error && <Banner tone="warn">Could not read worker heartbeats: {status.worker.error}</Banner>}
                    {status?.worker && (
                        <p className={styles.note}>
                            Setting source: <span className={tl.mono}>{status.worker.runnerSource}</span>. Move the pipeline with the runner switch above; the API
                            falls back to running a job itself whenever the worker cannot be reached.
                            {status.worker.runner === 'worker' && status.worker.live === 0 && ' No live worker has beaten in the last 3 minutes — jobs are running in the API until one comes back.'}
                        </p>
                    )}
                    {status?.worker && status.worker.heartbeats.length === 0 && !status.worker.error && (
                        <p className={styles.sub}>No worker has reported in the last 24 hours.</p>
                    )}
                    {status?.worker && liveWorkers.length === 0 && staleWorkers.length > 0 && (
                        <p className={styles.sub}>No live worker right now — only older heartbeats from the last 24 hours.</p>
                    )}
                    {settings?.groups.worker.error && <Banner tone="warn">Could not read the comms-worker secret: {settings.groups.worker.error}</Banner>}
                    {workerDraft && settings && (
                        <div className={styles.stack}>
                            <FieldGrid minColumn={200}>
                                <Field
                                    label={
                                        <>
                                            Worker{' '}
                                            <InfoTip text="Off = the worker stays up (health, buttons on this page) but its loops pause: no sweeping, no reading, no matching. Applies within a minute of saving." />
                                        </>
                                    }
                                >
                                    <Switch
                                        label={workerDraft.enabled ? 'Loops run' : 'Loops paused'}
                                        checked={workerDraft.enabled}
                                        disabled={Boolean(busy)}
                                        onChange={(ev) => editDraft({ enabled: ev.target.checked })}
                                    />
                                </Field>
                                <Field label="Poll every (ms)" hint="How often a loop checks for work">
                                    <Input small mono value={workerDraft.pollMs} disabled={Boolean(busy)} onChange={(ev) => editDraft({ pollMs: ev.target.value })} />
                                </Field>
                                <Field label="Heartbeat every (ms)" hint="How often the worker reports in">
                                    <Input small mono value={workerDraft.heartbeatMs} disabled={Boolean(busy)} onChange={(ev) => editDraft({ heartbeatMs: ev.target.value })} />
                                </Field>
                                <Field
                                    label="Worker URL"
                                    hint={
                                        settings.groups.worker.envOverrides.baseUrl
                                            ? `Locked: ${settings.groups.worker.envOverrides.baseUrl} overrides it on this API (a local override). Shown is the value in force.`
                                            : 'Where this API reaches the worker (the action server). Used within a minute of saving.'
                                    }
                                    span={2}
                                >
                                    {settings.groups.worker.envOverrides.baseUrl ? (
                                        <Input small mono readOnly disabled value={workerBaseUrl(settings) || '— not set —'} title="Locked by an environment override on this API" />
                                    ) : (
                                        <Input small mono value={workerDraft.baseUrl} disabled={Boolean(busy)} placeholder="https://…" onChange={(ev) => editDraft({ baseUrl: ev.target.value })} />
                                    )}
                                </Field>
                            </FieldGrid>
                            <Field
                                label={
                                    <>
                                        Loops <InfoTip text="Which background loops this worker runs. All ticked = every loop, including ones added later." />
                                    </>
                                }
                            >
                                <div className={tl.loopGrid}>
                                    {settings.workerLoops.map((name) => (
                                        <Checkbox
                                            key={name}
                                            label={name}
                                            disabled={Boolean(busy)}
                                            checked={workerDraft.loops === 'all' || (workerDraft.loops !== 'none' && workerDraft.loops.includes(name))}
                                            onChange={(ev) => toggleLoop(name, ev.target.checked)}
                                        />
                                    ))}
                                </div>
                            </Field>
                            <div className={styles.actions}>
                                <Button
                                    size="sm"
                                    disabled={!workerDirty || Boolean(busy)}
                                    onClick={() =>
                                        saveSettings(
                                            'worker',
                                            {
                                                enabled: workerDraft.enabled,
                                                loops: workerDraft.loops === 'all' ? 'all' : workerDraft.loops === 'none' ? 'none' : workerDraft.loops.join(','),
                                                pollMs: workerDraft.pollMs,
                                                heartbeatMs: workerDraft.heartbeatMs,
                                                ...(settings.groups.worker.envOverrides.baseUrl ? {} : { baseUrl: workerDraft.baseUrl }),
                                            },
                                            'Save worker settings',
                                        )
                                    }
                                >
                                    Save worker settings
                                </Button>
                                {workerDirty && (
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        disabled={Boolean(busy)}
                                        onClick={() => {
                                            setWorkerDraft(draftFromGroup(settings.groups.worker))
                                            setWorkerDirty(false)
                                        }}
                                    >
                                        Discard
                                    </Button>
                                )}
                                <span className={styles.sub}>
                                    Stored in <span className={tl.mono}>{settings.groups.worker.secretName}</span>. API key:{' '}
                                    {settings.groups.worker.hidden.apiKey?.set ? 'set' : 'not set'} (only changeable in AWS). Loops on/off and the loop list
                                    apply within a minute; poll and heartbeat intervals apply when the worker next starts.
                                </span>
                            </div>
                        </div>
                    )}
                    <div className={tl.schedules}>
                        {[...liveWorkers, ...(showStaleWorkers ? staleWorkers : [])].map((h) => (
                            <div key={h.workerId} className={tl.schedule}>
                                <div className={tl.scheduleHead}>
                                    <span>
                                        <StatusPill tone={h.stale ? 'bad' : 'good'} dot size="sm">
                                            {h.stale ? 'stale' : 'live'}
                                        </StatusPill>{' '}
                                        {h.workerId}
                                    </span>
                                    <span className={styles.sub}>beat {ago(h.beatAt, now)}</span>
                                </div>
                                <span className={tl.mono}>
                                    v{h.version ?? '?'} · core {h.coreVersion ?? '?'} · up since {fmtDate(h.startedAt)}
                                </span>
                                <span className={styles.sub}>
                                    {Object.values(h.loops || {})
                                        .map((l) => `${l.name} ${l.state}${l.lastFinishedAt ? ` (${ago(l.lastFinishedAt, now)})` : ''}`)
                                        .join(' · ') || 'no loop data'}
                                </span>
                                {h.lastError && <span className={styles.error}>{h.lastError}</span>}
                            </div>
                        ))}
                    </div>
                    {staleWorkers.length > 0 && (
                        <div className={styles.actions}>
                            <Button size="sm" variant="ghost" onClick={() => setShowStaleWorkers((v) => !v)} aria-expanded={showStaleWorkers}>
                                {showStaleWorkers
                                    ? 'Hide stale workers'
                                    : `Show ${staleWorkers.length} stale worker${staleWorkers.length === 1 ? '' : 's'}`}
                            </Button>
                            <span className={styles.sub}>
                                Stale means no heartbeat for over 3 minutes — usually an old local boot or a redeployed instance. They drop off after 24
                                hours.
                            </span>
                        </div>
                    )}
                </Card>
                )}

                {/* Automation — the API's own schedule; shown while the runner is api. */}
                {settings && effectiveRunner(settings) === 'api' && (
                <Card
                    muted
                    title="Automation"
                    subtitle="The cloud schedule (EventBridge) the case-delivery API runs on its own: a mailbox sweep and a reconcile, every 5 minutes. Both off = nothing runs in the background. The stages can still be run by hand at any time."
                >
                    {status?.automation.error && (
                        <Banner tone="warn">Could not read the EventBridge rules: {status.automation.error}</Banner>
                    )}
                    <div className={tl.schedules}>
                        {(status?.automation.schedules || []).map((s) => (
                            <div key={s.key} className={tl.setting}>
                                <div className={tl.settingHead}>
                                    <StatusPill tone={s.enabled ? 'good' : s.exists ? 'bad' : 'warn'} dot size="sm">
                                        {s.enabled ? 'on' : s.exists ? 'off' : 'missing'}
                                    </StatusPill>{' '}
                                    {s.key === 'sweep'
                                        ? 'Mailbox sweep'
                                        : 'Reconcile (preprocess · identity · extract · re-match)'}
                                </div>
                                {s.exists && (
                                    <div className={tl.settingControl}>
                                        <Switch
                                            label={s.enabled ? 'On' : 'Off'}
                                            checked={Boolean(s.enabled)}
                                            disabled={Boolean(busy)}
                                            onChange={(ev) => toggleSchedule(s, ev.target.checked)}
                                        />
                                    </div>
                                )}
                                <span className={tl.mono}>
                                    {s.rule} · {s.schedule || '—'} → {s.route}
                                </span>
                                <span className={styles.sub}>
                                    Last cloud run:{' '}
                                    {s.drives
                                        .map((name) => {
                                            const c = lastCron(name)
                                            return c
                                                ? `${name} ${ago(c.finished_at, now)} (${c.finished_reason})`
                                                : `${name} never`
                                        })
                                        .filter((_, i) => (s.key === 'reconcile' ? i === s.drives.length - 1 : true))
                                        .join(' · ')}
                                </span>
                                {s.error && <span className={styles.error}>{s.error}</span>}
                            </div>
                        ))}
                    </div>
                </Card>
                )}

                </div>
                )}
                </div>
            </Card>
            )}
        </div>
    )
}
